export const FINANCIAL_CAPABILITIES_PERMISSION_KEY = 'financialCapabilities'

export const FINANCIAL_CAPABILITIES = [
  'view',
  'manageRates',
  'manageRecords',
  'approve',
  'recordPayments',
  'export',
] as const

export type FinancialCapability = (typeof FINANCIAL_CAPABILITIES)[number]

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) {
    return null
  }
  return value.filter((item): item is string => typeof item === 'string')
}

function hasLegacyFinancialAccess(permissions: unknown): boolean {
  if (!isRecord(permissions)) {
    return false
  }
  return readStringArray(permissions.workforce)?.includes('edit') === true
}

export function resolveFinancialCapabilities(permissions: unknown): FinancialCapability[] {
  const config = isRecord(permissions) ? permissions : null
  const explicit = readStringArray(config?.[FINANCIAL_CAPABILITIES_PERMISSION_KEY])

  if (explicit === null) {
    return hasLegacyFinancialAccess(permissions) ? [...FINANCIAL_CAPABILITIES] : []
  }

  return FINANCIAL_CAPABILITIES.filter((capability) => explicit.includes(capability))
}

export function canUseFinancialCapability(
  permissions: unknown,
  capability: FinancialCapability
): boolean {
  const capabilities = resolveFinancialCapabilities(permissions)
  if (!capabilities.includes('view')) {
    return false
  }
  return capability === 'view' || capabilities.includes(capability)
}

export function nextFinancialCapabilities(
  permissions: unknown,
  capability: FinancialCapability,
  enabled: boolean
): FinancialCapability[] {
  const current = new Set(resolveFinancialCapabilities(permissions))

  if (capability === 'view' && !enabled) {
    return []
  }
  if (enabled) {
    current.add('view')
    current.add(capability)
  } else {
    current.delete(capability)
  }

  return FINANCIAL_CAPABILITIES.filter((item) => current.has(item))
}
